import type { SampleProduct } from '../api/types';

interface Props {
  products: SampleProduct[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

export default function SampleProductTabs({ products, selectedId, onSelect }: Props) {
  if (products.length === 0) {
    return (
      <div className="text-xs font-mono uppercase tracking-widish text-ff-mute py-4">
        No sample products loaded.
      </div>
    );
  }

  const fixtureCount = products.filter((p) => p.source === 'fixture').length;

  return (
    <div>
      {fixtureCount > 0 && (
        <div
          role="alert"
          data-testid="fixture-banner"
          className="mb-3 rounded-xl border border-ff-warning/30 bg-ff-warning-soft px-3 py-2 text-[12px] text-ff-warning"
        >
          {fixtureCount === products.length ? (
            <>
              <strong>Demo products.</strong> Your product source couldn't be reached
              or doesn't contain the configured sample IDs, so bundled examples are
              shown instead. The canvas is <em>not</em> previewing your real data.
            </>
          ) : (
            <>
              <strong>Some demo products.</strong> {fixtureCount} of {products.length}{' '}
              are bundled examples — their IDs weren't found in your product source.
            </>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="sample products">
      {products.map((p) => {
        const isSel = p.id === selectedId;
        return (
          <button
            key={p.id}
            role="tab"
            aria-selected={isSel}
            onClick={() => onSelect(p.id)}
            data-testid={`sample-${p.id}`}
            className={`group flex items-center gap-3 px-3 py-2 rounded-xl border transition-all ${
              isSel
                ? 'border-ff-orange bg-white shadow-soft'
                : 'border-ff-line bg-white/70 hover:bg-white hover:border-ff-ink/20'
            }`}
          >
            <span className="h-9 w-9 rounded-md overflow-hidden bg-ff-paper flex items-center justify-center shrink-0 border border-ff-ink/5">
              {p.image_url ? (
                <img
                  src={p.image_url}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : null}
            </span>
            <span className="text-left max-w-[160px]">
              <span className="block text-[11px] font-mono uppercase tracking-widish text-ff-mute truncate">
                #{p.id}
              </span>
              <span className="block text-xs font-medium text-ff-ink truncate">{p.title}</span>
            </span>
            {p.cutout_state === 'missing' ? (
              <span
                className="ff-chip bg-ff-danger-soft text-[10px] text-ff-danger"
                data-testid={`no-cutout-${p.id}`}
                title="No cutout is cached for this product — the preview composes the raw photo. Queue a re-cut on its QA card to render one."
              >
                no cutout
              </span>
            ) : null}
            {p.source === 'fixture' ? (
              <span
                className="ff-chip bg-ff-warning-soft text-[10px] text-ff-warning"
                title="Bundled demo product — not from your product source"
              >
                demo
              </span>
            ) : null}
            {p.discount_pct ? (
              <span className="ff-chip bg-ff-accent text-white text-[10px]">
                -{p.discount_pct}%
              </span>
            ) : null}
          </button>
        );
      })}
      </div>
    </div>
  );
}
